import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import AdminTemplates from '../../components/AdminTemplates';
import AdminHSBakbord from './AdminHSBakbord';
import './AdminPage.css';

const AdminPage = () => {
  const { 
    isAdmin, 
    isLoading, 
    error: adminError, 
    adminUsers, 
    fetchAdminUsers, 
    grantAdminRole, 
    revokeAdminRole 
  } = useAdmin();
  
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  
  // Refs for keyboard navigation
  const adminEmailInputRef = useRef(null);
  const grantAdminButtonRef = useRef(null);
  
  const navigate = useNavigate();

  // Use a ref to track if we've already fetched admin users
  const hasFetchedRef = React.useRef(false);

  useEffect(() => {
    console.log("AdminPage mounted or deps changed");
    console.log("isAdmin:", isAdmin);
    console.log("isLoading:", isLoading);
    
    // If not loading and not admin, redirect to home
    if (!isLoading && !isAdmin) {
      console.log("Not admin, redirecting to home");
      navigate('/');
      return;
    }
    
    // Fetch admin users only once when isAdmin becomes true and we haven't fetched yet
    if (isAdmin && !isLoading && !hasFetchedRef.current) {
      console.log("Is admin, fetching admin users");
      hasFetchedRef.current = true;
      fetchAdminUsers();
    }
  }, [isAdmin, isLoading, navigate, fetchAdminUsers]);
  
  // Add keyboard event listeners for better UX
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Handle Escape key to dismiss messages
      if (e.key === 'Escape') {
        setActionMessage('');
        setActionError('');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleGrantAdmin = async (e) => {
    e.preventDefault();
    setActionMessage('');
    setActionError('');
    
    if (!newAdminEmail.trim()) {
      setActionError('Please enter a valid email address');
      return;
    }
    
    try {
      const success = await grantAdminRole(newAdminEmail);
      if (success) {
        setActionMessage(`Admin privileges granted to ${newAdminEmail}`);
        setNewAdminEmail('');
      }
    } catch (err) {
      setActionError('Failed to grant admin privileges');
    }
  };

  const handleRevokeAdmin = async (userId, email) => {
    setActionMessage('');
    setActionError('');
    
    try {
      const success = await revokeAdminRole(userId);
      if (success) {
        setActionMessage(`Admin privileges revoked from ${email}`);
      }
    } catch (err) {
      setActionError('Failed to revoke admin privileges');
    }
  };

  // Only show loading on initial load, not during admin user fetching
  if (isLoading && !hasFetchedRef.current) {
    return <div className="admin-page-loading">Loading...</div>;
  }

  if (!isAdmin) {
    return <div className="admin-page-unauthorized">You do not have permission to access this page.</div>;
  }

  return (
    <div className="admin-page-container">
      <h1 className="admin-page-title">Admin Dashboard</h1>
      
      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Manage Admins
        </button>
        <button 
          className={`admin-tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Default Templates
        </button>
        <button 
          className={`admin-tab ${activeTab === 'hsbakbord' ? 'active' : ''}`}
          onClick={() => setActiveTab('hsbakbord')}
        >
          HSBakbord
        </button>
      </div>
      
      {adminError && <div className="admin-page-error">{adminError}</div>}
      {actionError && <div className="admin-page-error">{actionError}</div>}
      {actionMessage && <div className="admin-page-success">{actionMessage}</div>}
      
      {activeTab === 'users' && (
        <div className="admin-users-section">
          <h2>Manage Admin Users</h2>
          
          <form className="admin-grant-form" onSubmit={handleGrantAdmin}>
            <div className="admin-form-group">
              <label htmlFor="admin-email">Grant Admin Privileges:</label>
              <div className="admin-input-group">
                <input
                  id="admin-email"
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="Enter user email"
                  className="admin-email-input"
                  required
                />
                <button type="submit" className="admin-grant-btn">Grant Admin</button>
              </div>
            </div>
          </form>
          
          <div className="admin-users-list-container">
            <h3>Current Admins</h3>
            {adminUsers.length === 0 ? (
              <p className="admin-no-users">No admin users found.</p>
            ) : (
              <ul className="admin-users-list">
                {adminUsers.map(user => (
                  <li key={user.id} className="admin-user-item">
                    <div className="admin-user-info">
                      <span className="admin-user-email">{user.email}</span>
                      {user.username && <span className="admin-user-name">{user.username}</span>}
                    </div>
                    <button 
                      className="admin-revoke-btn"
                      onClick={() => handleRevokeAdmin(user.id, user.email)}
                    >
                      Revoke Admin
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'templates' && (
        <div className="admin-templates-section">
          <AdminTemplates />
        </div>
      )}
      
      {activeTab === 'hsbakbord' && (
        <div className="admin-hsbakbord-section">
          <AdminHSBakbord />
        </div>
      )}
    </div>
  );
};

export default AdminPage;